import React, { useRef, useState, useCallback } from 'react';
import {
  ganttMonths, ganttOffsetPct, ganttWidthPct,
  STATUS_CONFIG, TYPE_CONFIG, GANTT_START, GANTT_END,
  formatCurrency, formatAED, getRawBurn, getTeamSize, monthsBetween,
} from '../utils';

const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const ROW_HEIGHT = 38;
const LABEL_WIDTH = 280;

function GanttRow({ project, zoom, isChild, allProjects, developers }) {
  const [hovered, setHovered] = useState(false);
  const [tooltip, setTooltip] = useState({ x: 0, y: 0 });

  const statusCfg = STATUS_CONFIG[project.status] || STATUS_CONFIG['planned'];
  const typeCfg   = TYPE_CONFIG[project.type];
  const left  = ganttOffsetPct(project.startDate);
  const width = ganttWidthPct(project.startDate, project.endDate);
  const months = monthsBetween(project.startDate, project.endDate);
  const burn   = getRawBurn(project, developers);

  // Today marker position
  const todayPct = ganttOffsetPct(new Date().toISOString());

  const handleMouseMove = useCallback((e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltip({ x: e.clientX - rect.left + 12, y: e.clientY - rect.top - 8 });
  }, []);

  const barLabel = width * zoom > 8 ? (
    <span style={{
      position:'absolute', left:8, top:'50%', transform:'translateY(-50%)',
      color:'#fff', fontSize:11, fontWeight:700, whiteSpace:'nowrap',
      overflow:'hidden', maxWidth:`calc(100% - 16px)`,
      textShadow:'0 1px 2px rgba(0,0,0,0.4)'
    }}>
      {project.name} · {months}mo · {formatCurrency(burn)}
    </span>
  ) : null;

  return (
    <div style={{
      display:'flex', alignItems:'center',
      height: ROW_HEIGHT,
      borderBottom: '1px solid #1e293b',
      background: isChild ? '#0f172a' : '#0d1526',
    }}>
      {/* Gantt bar area */}
      <div
        style={{ flex:1, position:'relative', height:'100%', cursor:'pointer' }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onMouseMove={handleMouseMove}
      >
        {/* Weekend/month alternating bg stripes handled by parent */}

        {/* Bar */}
        <div style={{
          position:'absolute',
          left: `${left}%`,
          width: `${width}%`,
          top: '20%',
          height: '60%',
          background: hovered
            ? statusCfg.color
            : `${statusCfg.color}cc`,
          borderRadius: 4,
          border: `1.5px solid ${statusCfg.color}`,
          transition: 'background 0.15s',
          display:'flex', alignItems:'center',
          overflow:'hidden',
          boxShadow: hovered ? `0 0 12px ${statusCfg.color}88` : 'none',
        }}>
          {barLabel}
        </div>

        {/* Tooltip */}
        {hovered && (
          <div style={{
            position:'fixed',
            left: tooltip.x + LABEL_WIDTH,
            top: tooltip.y,
            background:'#1e293b',
            border:'1px solid #334155',
            borderRadius:8,
            padding:'10px 14px',
            zIndex:9999,
            fontSize:12,
            color:'#e2e8f0',
            minWidth:240,
            pointerEvents:'none',
            boxShadow:'0 8px 32px rgba(0,0,0,0.6)',
          }}>
            <div style={{fontWeight:700,fontSize:13,color:'#f1f5f9',marginBottom:6}}>
              {typeCfg.icon} {project.name}
            </div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'3px 12px'}}>
              <span style={{color:'#94a3b8'}}>Status:</span>
              <span style={{color:statusCfg.color,fontWeight:600}}>{statusCfg.label}</span>
              <span style={{color:'#94a3b8'}}>Start:</span>
              <span>{new Date(project.startDate).toLocaleDateString('en-US',{month:'short',year:'numeric'})}</span>
              <span style={{color:'#94a3b8'}}>End:</span>
              <span>{new Date(project.endDate).toLocaleDateString('en-US',{month:'short',year:'numeric'})}</span>
              <span style={{color:'#94a3b8'}}>Duration:</span>
              <span>{months} months</span>
              <span style={{color:'#94a3b8'}}>Team:</span>
              <span>{getTeamSize(project)} devs</span>
              <span style={{color:'#94a3b8'}}>Raw Burn:</span>
              <div>
                <div style={{color:'#fbbf24',fontWeight:700}}>{formatCurrency(burn)}</div>
                <div style={{color:'#64748b',fontSize:10}}>{formatAED(burn)}</div>
              </div>
            </div>
            {project.notes && (
              <div style={{marginTop:8,paddingTop:8,borderTop:'1px solid #334155',color:'#94a3b8',fontSize:11}}>
                📝 {project.notes}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default function GanttChart({ projects, developers }) {
  const [zoom, setZoom] = useState(1);
  const scrollRef = useRef(null);

  const months = ganttMonths();
  const totalMonths = months.length;

  // Group: main first, then its subs, then standalones
  const mainProjects = projects.filter(p => p.type === 'main');
  const standalone   = projects.filter(p => p.type === 'standalone');
  const ordered = [];
  for (const mp of mainProjects) {
    ordered.push(mp);
    const subs = projects.filter(p => p.parentId === mp.id);
    ordered.push(...subs);
  }
  ordered.push(...standalone);

  const colWidth = Math.round(38 * zoom); // px per month column
  const totalW   = colWidth * totalMonths;

  // Today position
  const todayMs    = Date.now();
  const ganttMs    = GANTT_END - GANTT_START;
  const todayPct   = Math.min(100, Math.max(0, (todayMs - GANTT_START) / ganttMs * 100));
  const todayLeft  = (todayPct / 100) * totalW;

  // Group months by year
  const years = {};
  months.forEach((m, i) => {
    const y = m.getFullYear();
    if (!years[y]) years[y] = [];
    years[y].push(i);
  });

  return (
    <div style={{ display:'flex', flexDirection:'column', height:'100%', fontFamily:'inherit' }}>
      {/* Zoom control */}
      <div style={{
        display:'flex', alignItems:'center', gap:12, padding:'8px 16px',
        borderBottom:'1px solid #1e293b', background:'#0a0f1e', flexShrink:0,
      }}>
        <span style={{color:'#64748b', fontSize:12, fontWeight:600}}>ZOOM</span>
        {[0.6,0.8,1,1.4,2,2.8].map(z => (
          <button
            key={z}
            onClick={() => setZoom(z)}
            style={{
              padding:'3px 10px', fontSize:12, fontWeight:700,
              borderRadius:4, border:'none', cursor:'pointer',
              background: zoom === z ? '#3b82f6' : '#1e293b',
              color: zoom === z ? '#fff' : '#64748b',
              transition:'all 0.15s',
            }}
          >{z}x</button>
        ))}
        <span style={{marginLeft:'auto', color:'#64748b', fontSize:11}}>
          Scroll → to navigate timeline &nbsp;·&nbsp; Hover bars for details
        </span>
        <div style={{display:'flex',alignItems:'center',gap:6,fontSize:11}}>
          <span style={{color:'#ef4444',fontWeight:700}}>▼</span>
          <span style={{color:'#64748b'}}>Today</span>
        </div>
      </div>

      <div style={{ display:'flex', flex:1, overflow:'hidden' }}>
        {/* Fixed left: project name column */}
        <div style={{
          width: LABEL_WIDTH, flexShrink:0,
          borderRight:'2px solid #1e293b',
          display:'flex', flexDirection:'column',
          background:'#0a0f1e',
        }}>
          {/* Header spacer */}
          <div style={{height:48, borderBottom:'1px solid #1e293b', display:'flex', flexDirection:'column', justifyContent:'flex-end'}}>
            <div style={{height:24, padding:'0 12px', display:'flex', alignItems:'center'}}>
              <span style={{fontSize:10, color:'#475569', fontWeight:700, letterSpacing:1}}>PROJECT</span>
            </div>
          </div>
          {/* Rows */}
          {ordered.map(p => {
            const statusCfg = STATUS_CONFIG[p.status] || STATUS_CONFIG['planned'];
            const typeCfg   = TYPE_CONFIG[p.type];
            const isChild   = p.type === 'sub';
            const isMain    = p.type === 'main';
            return (
              <div key={p.id} style={{
                height: ROW_HEIGHT,
                display:'flex', alignItems:'center',
                padding: isChild ? '0 8px 0 24px' : '0 8px 0 10px',
                borderBottom:'1px solid #1e293b',
                background: isMain ? '#0d1f3c' : isChild ? '#0f172a' : '#0d1526',
                gap:8,
              }}>
                <span style={{fontSize:11}}>{typeCfg.icon}</span>
                <div style={{flex:1, minWidth:0}}>
                  <div style={{
                    fontSize: isMain ? 12 : 11,
                    fontWeight: isMain ? 800 : 600,
                    color: isMain ? '#e2e8f0' : '#cbd5e1',
                    whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis',
                  }}>
                    {p.name}
                  </div>
                  {p.phase && (
                    <div style={{fontSize:10, color:'#475569'}}>{p.phase}</div>
                  )}
                </div>
                <div style={{
                  width:8, height:8, borderRadius:'50%', flexShrink:0,
                  background: statusCfg.color,
                  boxShadow: `0 0 4px ${statusCfg.color}`,
                }} />
              </div>
            );
          })}
        </div>

        {/* Scrollable right: gantt bars */}
        <div
          ref={scrollRef}
          style={{
            flex:1, overflowX:'auto', overflowY:'auto',
            position:'relative',
          }}
        >
          <div style={{ width: totalW, position:'relative' }}>
            {/* Year header */}
            <div style={{
              display:'flex', height:24,
              borderBottom:'1px solid #0f172a',
              position:'sticky', top:0, zIndex:10, background:'#0a0f1e',
            }}>
              {Object.entries(years).map(([year, idxs]) => (
                <div key={year} style={{
                  width: idxs.length * colWidth,
                  display:'flex', alignItems:'center', justifyContent:'center',
                  fontSize:11, fontWeight:800, color:'#94a3b8',
                  letterSpacing:2, borderRight:'1px solid #1e293b',
                  background:'#0a0f1e',
                }}>
                  {year}
                </div>
              ))}
            </div>

            {/* Month header */}
            <div style={{
              display:'flex', height:24,
              borderBottom:'1px solid #1e293b',
              position:'sticky', top:24, zIndex:10, background:'#0a0f1e',
            }}>
              {months.map((m, i) => {
                const isToday = m.getFullYear() === new Date().getFullYear() &&
                                m.getMonth()    === new Date().getMonth();
                return (
                  <div key={i} style={{
                    width: colWidth, flexShrink:0,
                    display:'flex', alignItems:'center', justifyContent:'center',
                    fontSize: colWidth > 28 ? 10 : 8,
                    fontWeight:700,
                    color: isToday ? '#ef4444' : (m.getMonth() % 3 === 0 ? '#94a3b8' : '#334155'),
                    borderRight:'1px solid #1a2436',
                    background: isToday ? '#1a0a0a' : 'transparent',
                    letterSpacing: colWidth > 32 ? 0.5 : 0,
                  }}>
                    {colWidth > 20 ? MONTH_NAMES[m.getMonth()] : (m.getMonth()%3===0 ? MONTH_NAMES[m.getMonth()] : '')}
                  </div>
                );
              })}
            </div>

            {/* Background grid + rows */}
            <div style={{ position:'relative' }}>
              {/* Column grid lines */}
              <div style={{ position:'absolute', inset:0, display:'flex', pointerEvents:'none' }}>
                {months.map((m, i) => (
                  <div key={i} style={{
                    width: colWidth, flexShrink:0, height:'100%',
                    borderRight:'1px solid #0f172a',
                    background: m.getMonth() % 2 === 0 ? 'rgba(255,255,255,0.01)' : 'transparent',
                  }} />
                ))}
              </div>

              {/* Today vertical line */}
              <div style={{
                position:'absolute',
                left: todayLeft,
                top:0, bottom:0,
                width:2,
                background:'#ef444488',
                zIndex:5,
                pointerEvents:'none',
              }}>
                <div style={{
                  position:'sticky', top:0,
                  background:'#ef4444',
                  color:'#fff',
                  fontSize:9, fontWeight:900,
                  padding:'2px 4px',
                  borderRadius:'0 4px 4px 0',
                  whiteSpace:'nowrap',
                }}>TODAY</div>
              </div>

              {/* Gantt rows */}
              {ordered.map((p, i) => {
                const left  = ganttOffsetPct(p.startDate);
                const width = ganttWidthPct(p.startDate, p.endDate);
                const months_dur = monthsBetween(p.startDate, p.endDate);
                const burn   = getRawBurn(p, developers);
                const statusCfg = STATUS_CONFIG[p.status] || STATUS_CONFIG['planned'];
                const isChild   = p.type === 'sub';
                const isMain    = p.type === 'main';

                return (
                  <GanttRow
                    key={p.id}
                    project={p}
                    zoom={zoom}
                    isChild={isChild}
                    allProjects={projects}
                    developers={developers}
                  />
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
